import {
  type BoatPart,
  type BoatView,
  partsForView,
} from "@nav-games/sailing";

/// Labelled boat-anatomy figure with four views — yacht profile,
/// rigging, powerboat profile, below-decks cutaway. Reference panels
/// render with `showAllLabels`; the recognition game highlights a
/// single part with `highlight={id}` and reveals the rest on answer.

interface Props {
  view: BoatView;
  /// When set, the named part is highlighted (bigger dot + accent
  /// colour) and is the only labelled part unless `showAllLabels`.
  highlight?: BoatPart["id"] | null;
  /// Show every part's label even when one is highlighted — used in
  /// the reveal panel after the student answers.
  showAllLabels?: boolean;
  /// Click handler — used by the "click the part" game variant.
  onClickPart?: (id: BoatPart["id"]) => void;
  size?: number;
}

const VW = 360;
const VH = 240;
const STROKE = "var(--text, #0f172a)";
const MUTED = "var(--text-muted, #64748b)";
const ACCENT = "#c0382b";

function scaleX(x: number): number {
  // x is 0..100 in the part data; map to viewbox with a small margin
  // for callout labels.
  return 28 + (x / 100) * (VW - 56);
}

function scaleY(y: number): number {
  return 18 + (y / 100) * (VH - 36);
}

/// Per-view callout offsets — push labels into clear space so they
/// don't sit on the hull. Default is +12 to the right for points in
/// the left half, −12 to the left for points on the right half.
function defaultLabelOffset(p: BoatPart): { dx: number; dy: number } {
  const dx = p.x < 50 ? -50 : 50;
  return { dx, dy: p.y < 30 ? -16 : p.y > 75 ? 18 : 0 };
}

export function BoatPartsFigure({
  view,
  highlight = null,
  showAllLabels = false,
  onClickPart,
  size = VW,
}: Props) {
  const parts = partsForView(view);
  const interactive = !!onClickPart;

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width={size}
      height={(size * VH) / VW}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`Boat anatomy — ${view}`}
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      <BackgroundForView view={view} />

      {parts.map((p) => {
        const isFocus = highlight === p.id;
        const shouldLabel = showAllLabels || !highlight || isFocus;
        const cx = scaleX(p.x);
        const cy = scaleY(p.y);
        const off = defaultLabelOffset(p);
        return (
          <g
            key={p.id}
            style={{ cursor: interactive ? "pointer" : "default" }}
            onClick={() => onClickPart?.(p.id)}
          >
            {interactive && (
              <circle
                cx={cx}
                cy={cy}
                r={14}
                fill="transparent"
                pointerEvents="all"
              />
            )}
            <circle
              cx={cx}
              cy={cy}
              r={isFocus ? 5 : 3}
              fill={isFocus ? ACCENT : STROKE}
              stroke={isFocus ? "white" : "none"}
              strokeWidth={isFocus ? 1.5 : 0}
            />
            {shouldLabel && !interactive && (
              <Callout
                cx={cx}
                cy={cy}
                dx={off.dx}
                dy={off.dy}
                text={p.name}
                emphasis={isFocus}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Callout({
  cx,
  cy,
  dx,
  dy,
  text,
  emphasis,
}: {
  cx: number;
  cy: number;
  dx: number;
  dy: number;
  text: string;
  emphasis: boolean;
}) {
  const tx = cx + dx;
  const ty = cy + dy;
  const anchor: "start" | "middle" | "end" =
    dx > 6 ? "start" : dx < -6 ? "end" : "middle";
  return (
    <>
      <line
        x1={cx}
        y1={cy}
        x2={tx}
        y2={ty}
        stroke={emphasis ? ACCENT : MUTED}
        strokeWidth={0.7}
      />
      <text
        x={tx + (anchor === "start" ? 2 : anchor === "end" ? -2 : 0)}
        y={ty + 3}
        fontSize={9}
        fill={emphasis ? ACCENT : "#1f2937"}
        fontWeight={emphasis ? 700 : 500}
        textAnchor={anchor}
      >
        {text}
      </text>
    </>
  );
}

/// ───────────────────────────────────────────────────────────────────
/// Background sketches — one per view. Intentionally simplified;
/// the labelled dots are the teaching tool, not the line-art.
/// ───────────────────────────────────────────────────────────────────

function BackgroundForView({ view }: { view: BoatView }) {
  if (view === "yacht-profile") return <YachtBackground />;
  if (view === "rigging") return <RiggingBackground />;
  if (view === "powerboat") return <PowerboatBackground />;
  return <BelowDecksBackground />;
}

function YachtBackground() {
  // Side profile: sweeping hull from a fine bow to a flat-ish stern.
  // Raised cabin (coachroof) midship with mast forward of it.
  // Waterline indicated by horizontal dashes near scaleY(76).
  return (
    <g>
      <line
        x1={scaleX(0)}
        y1={scaleY(76)}
        x2={scaleX(100)}
        y2={scaleY(76)}
        stroke="#9aa8c0"
        strokeDasharray="3 4"
        strokeWidth={1}
      />
      {/* Hull silhouette. */}
      <path
        d={`
          M ${scaleX(8)} ${scaleY(64)}
          Q ${scaleX(2)} ${scaleY(78)} ${scaleX(16)} ${scaleY(96)}
          L ${scaleX(86)} ${scaleY(96)}
          Q ${scaleX(96)} ${scaleY(82)} ${scaleX(92)} ${scaleY(70)}
          L ${scaleX(70)} ${scaleY(58)}
          Q ${scaleX(56)} ${scaleY(40)} ${scaleX(44)} ${scaleY(46)}
          L ${scaleX(30)} ${scaleY(58)}
          Z
        `}
        fill="#eef6ff"
        stroke="#475569"
        strokeWidth={1.5}
      />
      {/* Coachroof — raised cabin. */}
      <path
        d={`
          M ${scaleX(36)} ${scaleY(56)}
          L ${scaleX(64)} ${scaleY(48)}
          L ${scaleX(68)} ${scaleY(58)}
          Z
        `}
        fill="#dbe7f4"
        stroke="#475569"
        strokeWidth={1}
      />
      {/* Mast stub. */}
      <line
        x1={scaleX(34)}
        y1={scaleY(58)}
        x2={scaleX(36)}
        y2={scaleY(16)}
        stroke="#475569"
        strokeWidth={2}
      />
      {/* Rudder stub. */}
      <line
        x1={scaleX(92)}
        y1={scaleY(76)}
        x2={scaleX(92)}
        y2={scaleY(92)}
        stroke="#475569"
        strokeWidth={2.5}
      />
      {/* Lifelines — top stanchion line. */}
      <line
        x1={scaleX(10)}
        y1={scaleY(50)}
        x2={scaleX(92)}
        y2={scaleY(54)}
        stroke="#94a3b8"
        strokeWidth={0.8}
      />
    </g>
  );
}

function RiggingBackground() {
  // Mast vertical near x=20, masthead at top; boom horizontal from
  // gooseneck (x=22) to outhaul (x=82). Stays from masthead.
  return (
    <g>
      {/* Mast. */}
      <line
        x1={scaleX(20)}
        y1={scaleY(8)}
        x2={scaleX(20)}
        y2={scaleY(80)}
        stroke="#475569"
        strokeWidth={3}
      />
      {/* Boom. */}
      <line
        x1={scaleX(22)}
        y1={scaleY(80)}
        x2={scaleX(82)}
        y2={scaleY(80)}
        stroke="#475569"
        strokeWidth={2.5}
      />
      {/* Forestay — top of mast to bow at x=6 y=50. */}
      <line
        x1={scaleX(20)}
        y1={scaleY(8)}
        x2={scaleX(4)}
        y2={scaleY(54)}
        stroke="#94a3b8"
        strokeWidth={1.2}
      />
      {/* Backstay — top of mast to stern at x=38 y=50. */}
      <line
        x1={scaleX(20)}
        y1={scaleY(8)}
        x2={scaleX(40)}
        y2={scaleY(54)}
        stroke="#94a3b8"
        strokeWidth={1.2}
      />
      {/* Cap shrouds — top of mast to chainplates. */}
      <line
        x1={scaleX(20)}
        y1={scaleY(8)}
        x2={scaleX(34)}
        y2={scaleY(38)}
        stroke="#94a3b8"
        strokeWidth={1.2}
      />
      <line
        x1={scaleX(20)}
        y1={scaleY(8)}
        x2={scaleX(6)}
        y2={scaleY(38)}
        stroke="#94a3b8"
        strokeWidth={1.2}
      />
      {/* Spreaders. */}
      <line
        x1={scaleX(13)}
        y1={scaleY(38)}
        x2={scaleX(35)}
        y2={scaleY(38)}
        stroke="#475569"
        strokeWidth={1.5}
      />
      {/* Lower shrouds. */}
      <line
        x1={scaleX(20)}
        y1={scaleY(38)}
        x2={scaleX(30)}
        y2={scaleY(64)}
        stroke="#94a3b8"
        strokeWidth={1.2}
      />
      {/* Deck level. */}
      <line
        x1={scaleX(0)}
        y1={scaleY(64)}
        x2={scaleX(100)}
        y2={scaleY(64)}
        stroke="#9aa8c0"
        strokeDasharray="3 4"
        strokeWidth={1}
      />
      {/* Mainsail outline — light grey. */}
      <path
        d={`
          M ${scaleX(20)} ${scaleY(12)}
          L ${scaleX(22)} ${scaleY(78)}
          L ${scaleX(80)} ${scaleY(78)}
          Z
        `}
        fill="rgba(238, 246, 255, 0.4)"
        stroke="#cbd5e1"
        strokeWidth={1}
      />
    </g>
  );
}

function PowerboatBackground() {
  // Planing hull with a deep-V at the stern, console midships,
  // outboard at transom.
  return (
    <g>
      {/* Waterline. */}
      <line
        x1={scaleX(0)}
        y1={scaleY(76)}
        x2={scaleX(100)}
        y2={scaleY(76)}
        stroke="#9aa8c0"
        strokeDasharray="3 4"
        strokeWidth={1}
      />
      {/* Hull — planing profile. */}
      <path
        d={`
          M ${scaleX(6)} ${scaleY(60)}
          Q ${scaleX(0)} ${scaleY(70)} ${scaleX(10)} ${scaleY(92)}
          L ${scaleX(88)} ${scaleY(92)}
          L ${scaleX(92)} ${scaleY(68)}
          L ${scaleX(80)} ${scaleY(54)}
          Q ${scaleX(40)} ${scaleY(40)} ${scaleX(20)} ${scaleY(46)}
          Z
        `}
        fill="#eef6ff"
        stroke="#475569"
        strokeWidth={1.5}
      />
      {/* Console / windscreen. */}
      <path
        d={`
          M ${scaleX(50)} ${scaleY(48)}
          L ${scaleX(50)} ${scaleY(36)}
          L ${scaleX(64)} ${scaleY(36)}
          L ${scaleX(66)} ${scaleY(48)}
        `}
        fill="#dbe7f4"
        stroke="#475569"
        strokeWidth={1}
      />
      {/* Wheel symbol. */}
      <circle
        cx={scaleX(60)}
        cy={scaleY(42)}
        r={3}
        fill="none"
        stroke="#475569"
        strokeWidth={0.8}
      />
      {/* Outboard. */}
      <path
        d={`
          M ${scaleX(92)} ${scaleY(60)}
          L ${scaleX(96)} ${scaleY(60)}
          L ${scaleX(96)} ${scaleY(86)}
          L ${scaleX(92)} ${scaleY(86)}
          Z
        `}
        fill="#475569"
      />
    </g>
  );
}

function BelowDecksBackground() {
  // Cutaway: hull outline + interior compartment boxes.
  return (
    <g>
      {/* Hull. */}
      <path
        d={`
          M ${scaleX(4)} ${scaleY(20)}
          L ${scaleX(96)} ${scaleY(20)}
          L ${scaleX(96)} ${scaleY(80)}
          Q ${scaleX(50)} ${scaleY(96)} ${scaleX(4)} ${scaleY(80)}
          Z
        `}
        fill="#eef6ff"
        stroke="#475569"
        strokeWidth={1.5}
      />
      {/* Engine bay — a rectangle near centre. */}
      <rect
        x={scaleX(42)}
        y={scaleY(40)}
        width={scaleX(64) - scaleX(42)}
        height={scaleY(70) - scaleY(40)}
        fill="#cbd5e1"
        stroke="#475569"
        strokeWidth={1}
      />
      {/* Battery compartment. */}
      <rect
        x={scaleX(72)}
        y={scaleY(24)}
        width={scaleX(90) - scaleX(72)}
        height={scaleY(58) - scaleY(24)}
        fill="#fef3c7"
        stroke="#475569"
        strokeWidth={1}
      />
      {/* Fuel tank. */}
      <rect
        x={scaleX(12)}
        y={scaleY(60)}
        width={scaleX(28) - scaleX(12)}
        height={scaleY(80) - scaleY(60)}
        fill="#fde68a"
        stroke="#475569"
        strokeWidth={1}
      />
      {/* Water tank. */}
      <rect
        x={scaleX(12)}
        y={scaleY(22)}
        width={scaleX(34) - scaleX(12)}
        height={scaleY(38) - scaleY(22)}
        fill="#bae6fd"
        stroke="#475569"
        strokeWidth={1}
      />
      {/* Bilge — bottom curve. */}
      <line
        x1={scaleX(20)}
        y1={scaleY(86)}
        x2={scaleX(80)}
        y2={scaleY(86)}
        stroke="#94a3b8"
        strokeDasharray="2 3"
        strokeWidth={0.8}
      />
    </g>
  );
}
